/**
 * app/momentoSeguro.ts — reglas puras de SAFE_AUTO_UPDATE_V2 (sin temporizadores ni adivinanzas).
 *
 * Operación abierta = carrito con productos, tickets en espera (viven en memoria), ventana abierta (cobro,
 * postventa, cierre, reparación/garantía, movimientos: capas `fixed inset-0`) o un formulario con algo
 * tecleado fuera de la caja (se marca al teclear y se limpia al cambiar de pantalla).
 * Sin operación = sin operación abierta y sin escrituras por sincronizar (outbox / ventas sin internet).
 * Caja libre (actualización normal) = sin operación y en la pantalla de venta o en el login.
 * Momento seguro = caja libre y quieta 3 minutos o en segundo plano.
 * Cuántas pestañas hay NO se decide aquí: lo cuenta el service worker (public/sw-celmaxx.js).
 */
export const INACTIVO_MS = 3 * 60_000
export const RUTAS_SEGURAS = ['/pos', '/login']

export interface EstadoCaja {
  carrito: number
  enEspera: number
  hayVentana: boolean
  formularioSucio: boolean
  pendientesSync: number
  ruta: string
  inactivoMs: number
  oculta: boolean
}

export function operacionAbierta(s: EstadoCaja): boolean {
  return s.carrito > 0 || s.enEspera > 0 || s.hayVentana || s.formularioSucio
}

export function sinOperacion(s: EstadoCaja): boolean {
  return !operacionAbierta(s) && s.pendientesSync === 0
}

export function cajaLibre(s: EstadoCaja): boolean {
  return sinOperacion(s) && RUTAS_SEGURAS.includes(s.ruta)
}

export function momentoSeguro(s: EstadoCaja): boolean {
  return cajaLibre(s) && (s.oculta || s.inactivoMs >= INACTIVO_MS)
}

/** Cuándo pedir al service worker que se active: obligatoria ⇒ en cuanto no haya operación; normal ⇒ momento seguro. */
export function debeActivar(s: EstadoCaja, pendiente: boolean, obligatoria: boolean): boolean {
  if (!pendiente) return false
  return obligatoria ? sinOperacion(s) : momentoSeguro(s)
}

/**
 * La pantalla de «Actualización obligatoria» aparece en cuanto no hay operación abierta: nunca encima de una
 * (carrito, tickets en espera, ventana, formulario tecleado), pero en el instante en que termina. Sin conexión
 * no bloquea.
 */
export function gateVisible(s: EstadoCaja, obligatoria: boolean, enLinea: boolean): boolean {
  return obligatoria && enLinea && !operacionAbierta(s)
}

export interface VersionPublicada {
  gitSha: string
  buildTime: string
  minimoObligatorio?: string
}

/** current_build < minimum_required_build. Sin datos del servidor (sin red) ⇒ no es obligatoria. */
export function esObligatoria(
  mia: { buildTime: string },
  servidor: VersionPublicada | null
): boolean {
  if (!servidor?.minimoObligatorio) return false
  const minimo = Date.parse(servidor.minimoObligatorio)
  const propia = Date.parse(mia.buildTime)
  return Number.isFinite(minimo) && Number.isFinite(propia) && propia < minimo
}

export function hayVersionNueva(
  mia: { gitSha: string },
  servidor: VersionPublicada | null
): boolean {
  return !!servidor && !!servidor.gitSha && servidor.gitSha !== mia.gitSha
}

export type Paso = 'activar' | 'bajando' | 'recargar' | 'cerrar' | 'nada'

/**
 * Qué hacer cuando la caja ya puede actualizarse (staging 14-oct): hay versión en espera ⇒ pedir activarla;
 * bajando, o sin haber revisado aún el service worker contra la versión publicada ⇒ esperar; NADA en espera ni
 * bajando pero publicada otra ⇒ el navegador ya activó la nueva por su cuenta (o no la pudo instalar) ⇒ recargar
 * UNA sola vez; si esa recarga ya se hizo y seguimos viejos ⇒ pedir cerrar y abrir la pestaña (nunca en bucle).
 */
export function siguientePaso(s: {
  enEspera: boolean
  bajando: boolean
  hayNueva: boolean
  revisado: boolean
  yaRecargo: boolean
}): Paso {
  if (s.enEspera) return 'activar'
  if (!s.hayNueva) return 'nada'
  if (s.bajando || !s.revisado) return 'bajando'
  return s.yaRecargo ? 'cerrar' : 'recargar'
}

/** Ventanas y cajones abiertos (capas `fixed inset-0` visibles), sin contar la propia pantalla de actualización. */
export function hayVentanaAbierta(doc: Document = document): boolean {
  return Array.from(
    doc.querySelectorAll<HTMLElement>('.fixed.inset-0:not([data-celmaxx-gate])')
  ).some(el => {
    const r = el.getBoundingClientRect()
    const cs = getComputedStyle(el)
    return r.width > 0 && r.height > 0 && cs.visibility !== 'hidden' && cs.opacity !== '0'
  })
}
